import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import StarIcon from '@mui/icons-material/Star';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import api from '../utils/api';
import { formatDollars } from '../utils/money';
import ChurnSurveyDialog from '../components/modals/ChurnSurveyDialog';
// Paywall renders OUTSIDE OfflineProvider (mixed route), so useOffline() would
// silently default to online here — read the connectivity module directly,
// always paired with the OFFLINE_ENABLED flag.
import { OFFLINE_ENABLED } from '../offline/entities';
import {
  isEffectivelyOnline,
  subscribeConnectivity,
  reportConnectivitySuccess,
  reportFromError,
} from '../offline/connectivity';
const SUBS_ENABLED = process.env.REACT_APP_ENABLE_SUBSCRIPTIONS === 'true';
// Sales' demo-booking page, offered to clinics that just signed up (no trial yet).
export const BOOK_DEMO_URL = 'https://calendly.com/halle-hudson-asteris';

interface PricingTier {
  id: string;
  name: string;
  price: number;
  stripePriceId: string;
  features: string[];
  popular?: boolean;
  description: string;
  aiCallsPerMonth?: number;
}

// End-of-trial retention offer — shape of GET /subscription/retention-offer.
// Eligibility is decided server-side (trial expired, never paid, unredeemed).
interface RetentionOffer {
  eligible: boolean;
  percentOff: number;
  durationMonths: number;
  alreadyRedeemed: boolean;
}

const pricingTiers: PricingTier[] = [
  {
    id: 'basic',
    name: 'Basic',
    price: 199,
    stripePriceId: process.env.REACT_APP_STRIPE_BASIC_PRICE_ID || '',
    description: 'Core features for everyday equine practice management.',
    features: [
      'Unlimited vets',
      'Unlimited AI assistant',
      'Advanced scheduling',
      'Inventory',
      'Complete clinical records',
      'Advanced billing',
    ],
    aiCallsPerMonth: -1,
  },
];

const Paywall = () => {
  const navigate = useNavigate();
  // Immediately redirect if subscriptions disabled
  useEffect(() => {
    if (!SUBS_ENABLED) {
      navigate('/', { replace: true });
    }
  }, []);

  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(null);
  const [trialExpired, setTrialExpired] = useState(false);
  // A new signup stays PENDING until a trial is granted or a plan is bought.
  const [isNewSignup, setIsNewSignup] = useState(false);
  // Past-due recovery: a failed renewal must lead to "fix your payment method"
  // on the EXISTING subscription, never to re-purchasing (duplicate subs).
  const [pastDue, setPastDue] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [retentionOffer, setRetentionOffer] = useState<RetentionOffer | null>(null);
  const [offerCollapsed, setOfferCollapsed] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [surveyDone, setSurveyDone] = useState(false);
  const [isOffline, setIsOffline] = useState(
    () => OFFLINE_ENABLED && !isEffectivelyOnline(),
  );

  useEffect(() => {
    checkExistingSubscription();
  }, []);

  // Track effective connectivity so the offline notice clears live, and re-run
  // the subscription check on reconnect (same pattern as SubscriptionGuard) —
  // a vet who lands here offline may actually be subscribed.
  useEffect(() => {
    if (!OFFLINE_ENABLED) return undefined;
    const update = () => {
      const online = isEffectivelyOnline();
      setIsOffline(!online);
      if (online) void checkExistingSubscription();
    };
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    const unsubscribe = subscribeConnectivity(update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
      unsubscribe();
    };
  }, []);

  const checkExistingSubscription = async () => {
    try {
      const response = await api.get('/subscription/status');
      // This route renders OUTSIDE OfflineProvider, so nothing else reports
      // reachability here — feed our own round-trip back so a latched
      // observed-down state self-corrects (clears the offline banner).
      if (OFFLINE_ENABLED) reportConnectivitySuccess();
      const {
        status,
        subscriptionId,
        canManage: manage,
        isFreeTrial,
        trialDaysRemaining: days,
        trialExpired: expired,
      } = response.data;

      // If user has a paid subscription (ACTIVE/TRIALING with a Stripe subscription), redirect
      if ((status === 'ACTIVE' || status === 'TRIALING') && subscriptionId) {
        navigate('/');
        return;
      }

      setPastDue(status === 'PAST_DUE');
      setIsNewSignup(status === 'PENDING');
      setCanManage(!!manage);

      // Capture trial state for messaging
      if (isFreeTrial) {
        setTrialDaysRemaining(days);
      }
      if (expired) {
        setTrialExpired(true);
      }

      // Retention offer: the server decides eligibility (trial expired, never
      // paid, unredeemed). Fetch failure just means no offer UI — this is an
      // online-only surface and checkout is disabled offline anyway.
      try {
        const offerResponse = await api.get('/subscription/retention-offer');
        setRetentionOffer(offerResponse.data);
      } catch {
        setRetentionOffer(null);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      if (OFFLINE_ENABLED) reportFromError(error);
    } finally {
      setCheckingSubscription(false);
    }
  };

  const offerActive = !!retentionOffer?.eligible;
  const discountedPrice = (price: number) =>
    retentionOffer ? price * (1 - retentionOffer.percentOff / 100) : price;

  const handleSubscribe = async (tier: PricingTier) => {
    setLoading(true);
    setSelectedTier(tier.id);
    setError('');

    try {
      // Create checkout session; the server re-verifies offer eligibility
      const response = await api.post('/subscription/create-checkout-session', {
        priceId: tier.stripePriceId,
        tierName: tier.name,
        applyRetentionOffer: offerActive,
      });

      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error: any) {
      console.error('Subscription error:', error);
      // The clinic already holds a live subscription — re-check status so the
      // page flips into the past-due recovery view (or redirects if active).
      if (error.response?.status === 409 && error.response?.data?.code === 'SUBSCRIPTION_EXISTS') {
        setError('This clinic already has a subscription — no new purchase is needed.');
        setLoading(false);
        setSelectedTier(null);
        void checkExistingSubscription();
        return;
      }
      setError(
        error.response?.data?.message ||
        'Failed to start subscription. Please try again.'
      );
      setLoading(false);
      setSelectedTier(null);
      // Self-heal stale offer state (e.g. redeemed in another tab): re-check
      // eligibility so the hero and struck pricing collapse instead of lying.
      if (offerActive) {
        try {
          const offerResponse = await api.get('/subscription/retention-offer');
          setRetentionOffer(offerResponse.data);
        } catch {
          // keep current state — the claim already failed loudly above
        }
      }
    }
  };

  // Stripe Billing Portal: update the card / pay the open invoice on the
  // existing subscription. Access resumes via the invoice.payment_succeeded
  // webhook once the payment clears.
  const handleFixPayment = async () => {
    setPortalLoading(true);
    setError('');
    try {
      const response = await api.post('/subscription/billing-portal');
      if (response.data.url) {
        window.location.href = response.data.url;
        return;
      }
      throw new Error('No portal URL returned');
    } catch (error: any) {
      console.error('Billing portal error:', error);
      setError(
        error.response?.data?.message ||
        'Failed to open the billing portal. Please try again.'
      );
      setPortalLoading(false);
    }
  };

  if (!SUBS_ENABLED) {
    return null;
  }

  if (checkingSubscription) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 8 }}>
        {isOffline && (
          <Alert severity="info" sx={{ mb: 4 }}>
            You're offline. You can subscribe once you reconnect.
          </Alert>
        )}

        {pastDue ? (
          /* Past-due recovery: fix the payment method on the EXISTING
             subscription — the pricing grid is deliberately not rendered so a
             past-due clinic can't buy a duplicate subscription. */
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h1" gutterBottom>
              Payment failed — your subscription is on hold
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              A renewal charge for your clinic&apos;s subscription didn&apos;t go through.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3, maxWidth: 560, mx: 'auto' }}>
                {error}
              </Alert>
            )}

            <Card sx={{ maxWidth: 560, mx: 'auto', mt: 2, textAlign: 'left' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Your data is safe — nothing has been deleted. Access resumes
                  automatically as soon as the outstanding payment succeeds.
                </Typography>
                {canManage ? (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Update your clinic&apos;s payment method or pay the open invoice
                      in the secure Stripe billing portal.
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleFixPayment}
                      disabled={portalLoading || isOffline}
                      sx={{
                        bgcolor: '#C9772B',
                        '&:hover': { bgcolor: '#6B3410' },
                      }}
                    >
                      {portalLoading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Update payment method'
                      )}
                    </Button>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Ask your clinic owner to update the clinic&apos;s payment method —
                    access resumes automatically once the payment succeeds.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        ) : (
        <>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          {trialExpired ? (
            <>
              <Typography variant="h3" component="h1" gutterBottom>
                Your Free Trial Has Ended
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                Subscribe to continue using StableTrack
              </Typography>
            </>
          ) : trialDaysRemaining != null ? (
            <>
              <Typography variant="h3" component="h1" gutterBottom>
                One plan, everything included
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                You have {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} left in your free trial.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Subscribe now to ensure uninterrupted access when your trial ends.
              </Typography>
              <Button
                variant="text"
                onClick={() => navigate('/')}
                sx={{ color: '#C9772B', textTransform: 'none', fontWeight: 500 }}
              >
                Back to app
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h3" component="h1" gutterBottom>
                One plan, everything included
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                All of StableTrack for one monthly price
              </Typography>
            </>
          )}
        </Box>

        {/* End-of-trial retention offer hero */}
        {offerActive && !offerCollapsed && retentionOffer && (
          <Box
            sx={{
              mb: 5,
              mx: 'auto',
              maxWidth: 640,
              borderRadius: 3,
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 60%, #CD853F 100%)',
              color: '#FAF9F6',
              p: { xs: 3, md: 4 },
              textAlign: 'center',
              boxShadow: '0 8px 24px rgba(139, 69, 19, 0.25)',
            }}
          >
            <Typography
              variant="overline"
              sx={{ letterSpacing: 2, fontWeight: 700, color: '#FFD9A0' }}
            >
              One-time offer
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, mt: 0.5 }}>
              {retentionOffer.percentOff}% off for {retentionOffer.durationMonths} months
            </Typography>
            <Typography variant="body1" sx={{ mt: 1.5, opacity: 0.9 }}>
              Subscribe below and the discount applies automatically at checkout.
              Your horses, visits, and invoices are all right where you left them.
            </Typography>
            <Box
              sx={{
                mt: 2.5,
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              {/* Owner-only: declining opens the churn survey, and POST /subscription/churn-survey
                  now refuses a non-OWNER (it was the one write on that controller any clinic
                  member could reach). This block is gated on `offerActive`, NOT `canManage`, so
                  without this a vet on a lapsed clinic's paywall would meet a raw
                  "Only clinic owners can manage subscriptions" inside a feedback form. */}
              {canManage && (
                <Button
                  variant="text"
                  onClick={() => (surveyDone ? setOfferCollapsed(true) : setSurveyOpen(true))}
                  sx={{ color: 'rgba(250, 249, 246, 0.75)', textTransform: 'none' }}
                >
                  No thanks, I&apos;ll pass
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* Collapsed offer reminder */}
        {offerActive && offerCollapsed && (
          <Alert
            severity="info"
            sx={{ mb: 4, maxWidth: 640, mx: 'auto' }}
            action={
              <Button size="small" onClick={() => setOfferCollapsed(false)} sx={{ textTransform: 'none' }}>
                Show offer
              </Button>
            }
          >
            Your {retentionOffer?.percentOff}% off for {retentionOffer?.durationMonths} months is
            still available — it applies automatically at checkout.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {/* Pricing Cards */}
        <Grid container spacing={4} alignItems="flex-start" justifyContent="center" sx={{ position: 'relative', mt: 4 }}>
          {pricingTiers.map((tier) => (
            <Grid 
              item 
              xs={12} 
              md={6}
              lg={5}
              key={tier.id}
              sx={tier.popular ? {
                mt: { xs: 4, md: -2 },
                mb: { xs: 4, md: 0 },
                zIndex: 1,
                position: 'relative'
              } : {}}
            >
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: tier.popular ? '2px solid #C9772B' : '1px solid #e0e0e0',
                  boxShadow: tier.popular ? '0 8px 24px rgba(201, 119, 43, 0.15)' : 'none',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: tier.popular 
                      ? '0 12px 32px rgba(201, 119, 43, 0.2)' 
                      : '0 8px 24px rgba(0, 0, 0, 0.1)',
                  }
                }}
              >
                {tier.popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 35,
                      right: -47,
                      transform: 'rotate(45deg)',
                      backgroundColor: '#C9772B',
                      padding: '8px 40px',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      boxShadow: '0 2px 8px rgba(201, 119, 43, 0.25)',
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        bottom: -5,
                        borderLeft: '5px solid #5c2d0c',
                        borderBottom: '5px solid transparent',
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        bottom: -5,
                        borderRight: '5px solid #5c2d0c',
                        borderBottom: '5px solid transparent',
                      }
                    }}
                  >
                    <StarIcon sx={{ fontSize: 16 }} />
                    MOST POPULAR
                  </Box>
                )}
                
                <CardContent sx={{ flexGrow: 1, pt: tier.popular ? 4 : 2 }}>
                  <Typography variant="h5" component="h2" gutterBottom align="center">
                    {tier.name}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                    {tier.description}
                  </Typography>
                  
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'flex-start' }}>
                      {offerActive && (
                        <Typography
                          variant="h5"
                          component="span"
                          sx={{
                            textDecoration: 'line-through',
                            color: 'text.disabled',
                            mt: 1.5,
                            mr: 1,
                          }}
                        >
                          ${tier.price}
                        </Typography>
                      )}
                      <Typography
                        variant="h3"
                        component="span"
                        sx={{
                          fontWeight: tier.popular ? 700 : 600,
                          color: tier.popular ? '#C9772B' : 'inherit'
                        }}
                      >
                        {offerActive ? formatDollars(discountedPrice(tier.price)) : `$${tier.price}`}
                      </Typography>
                      <Typography
                        variant="h6"
                        component="span"
                        color="text.secondary"
                        sx={{ mt: 1, ml: 0.5 }}
                      >
                        /month
                      </Typography>
                    </Box>
                    {offerActive && retentionOffer && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        for {retentionOffer.durationMonths} months, then ${tier.price}/month
                      </Typography>
                    )}
                  </Box>

                  {tier.aiCallsPerMonth && (
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Chip
                        label={
                          tier.aiCallsPerMonth === -1
                            ? 'Unlimited AI Calls'
                            : `${tier.aiCallsPerMonth} AI Calls/month`
                        }
                        size="small"
                        sx={{
                          bgcolor: tier.aiCallsPerMonth === -1 ? 'rgba(201, 119, 43, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                          color: tier.aiCallsPerMonth === -1 ? '#C9772B' : 'text.secondary',
                          fontWeight: tier.aiCallsPerMonth === -1 ? 600 : 'normal',
                          '& .MuiChip-label': {
                            px: 1.5
                          }
                        }}
                      />
                    </Box>
                  )}

                  <List dense>
                    {tier.features.map((feature, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            variant: 'body2',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>

                <Box sx={{ p: 2 }}>
                  <Button
                    fullWidth
                    variant={tier.popular ? 'contained' : 'outlined'}
                    onClick={() => handleSubscribe(tier)}
                    disabled={loading || isOffline}
                    sx={{
                      bgcolor: tier.popular ? '#C9772B' : 'transparent',
                      borderColor: '#C9772B',
                      color: tier.popular ? 'white' : '#C9772B',
                      '&:hover': {
                        bgcolor: tier.popular ? '#6B3410' : 'rgba(201, 119, 43, 0.04)',
                        borderColor: '#C9772B',
                      },
                    }}
                  >
                    {loading && selectedTier === tier.id ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : offerActive && retentionOffer ? (
                      `Claim ${retentionOffer.percentOff}% off`
                    ) : (
                      'Get Started'
                    )}
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {isNewSignup && (
          <Box sx={{ textAlign: 'center', mt: 6, maxWidth: 640, mx: 'auto' }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Want to try StableTrack first? Book a demo and we&apos;ll set you up with a free trial.
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              href={BOOK_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Book a demo
            </Button>
          </Box>
        )}
        </>
        )}
      </Box>

      <ChurnSurveyDialog
        open={surveyOpen}
        onClose={() => setSurveyOpen(false)}
        onSubmitted={() => {
          setSurveyOpen(false);
          setSurveyDone(true);
          setOfferCollapsed(true);
        }}
      />
    </Container>
  );
};

export default Paywall;
